<!-- Availability Management Component -->
<div class="availability-management">
  <div class="page-header">
    <h2>Gestionarea Disponibilității pentru Programări</h2>
    <p class="subtitle">Configurați intervalele orare în care sunteți disponibil pentru activități și programări.</p>
  </div>

  <!-- Loading State -->
  <div class="loading-container" *ngIf="isLoading">
    <mat-progress-spinner diameter="40"></mat-progress-spinner>
    <p>Se încarcă programul de disponibilitate...</p>
  </div>

  <!-- Main Form -->
  <div class="availability-form" *ngIf="!isLoading">
    <form [formGroup]="availabilityForm" (ngSubmit)="saveAvailability()">
      
      <!-- Weekly Schedule Section -->
      <mat-card class="schedule-section">
        <mat-card-header>
          <mat-card-title>Program Săptămânal</mat-card-title>
          <mat-card-subtitle>Setați intervalele orare pentru fiecare zi din săptămână</mat-card-subtitle>
        </mat-card-header>
        
        <mat-card-content>
          <div class="days-container" formArrayName="days">
            <div class="day-schedule" 
                 *ngFor="let dayGroup of getDaysArray().controls; let dayIndex = index" 
                 [formGroupName]="dayIndex">
              
              <!-- Day Header -->
              <div class="day-header">
                <div class="day-toggle">
                  <mat-slide-toggle 
                    formControlName="enabled"
                    [color]="'primary'">
                    <span class="day-name">{{ getDayName(dayIndex + 1) }}</span>
                  </mat-slide-toggle>
                </div>
                
                <div class="day-actions" *ngIf="isDayEnabled(dayIndex)">
                  <button type="button" 
                          mat-stroked-button 
                          (click)="addTimeSlot(dayIndex)"
                          class="add-slot-btn">
                    <mat-icon>add</mat-icon>
                    Adaugă interval
                  </button>
                  
                  <button type="button" 
                          mat-icon-button 
                          [matMenuTriggerFor]="dayMenu"
                          class="menu-btn">
                    <mat-icon>more_vert</mat-icon>
                  </button>
                  
                  <mat-menu #dayMenu="matMenu">
                    <button mat-menu-item (click)="applyToAllDays(dayIndex)">
                      <mat-icon>content_copy</mat-icon>
                      <span>Aplică la toate zilele</span>
                    </button>
                    <button mat-menu-item (click)="applyWorkingDaysOnly(dayIndex)">
                      <mat-icon>work</mat-icon>
                      <span>Aplică la zilele lucrătoare</span>
                    </button>
                  </mat-menu>
                </div>
              </div>

              <!-- Time Slots -->
              <div class="time-slots" *ngIf="isDayEnabled(dayIndex)" formArrayName="slots">
                <div class="time-slot" 
                     *ngFor="let slotGroup of getSlotsArray(dayIndex).controls; let slotIndex = index" 
                     [formGroupName]="slotIndex">
                  
                  <div class="time-inputs">
                    <mat-form-field appearance="outline" class="time-field">
                      <mat-label>Ora început</mat-label>
                      <mat-select formControlName="start_time">
                        <mat-option *ngFor="let time of timeSlots" [value]="time">
                          {{ time }}
                        </mat-option>
                      </mat-select>
                    </mat-form-field>

                    <span class="time-separator">—</span>

                    <mat-form-field appearance="outline" class="time-field">
                      <mat-label>Ora sfârșit</mat-label>
                      <mat-select formControlName="end_time">
                        <mat-option *ngFor="let time of timeSlots" [value]="time">
                          {{ time }}
                        </mat-option>
                      </mat-select>
                    </mat-form-field>
                  </div>

                  <mat-form-field appearance="outline" class="notes-field">
                    <mat-label>Observații (opțional)</mat-label>
                    <input matInput formControlName="notes" placeholder="Ex: Doar consultanță"/>
                  </mat-form-field>

                  <div class="slot-actions">
                    <button type="button" 
                            mat-icon-button 
                            color="warn"
                            (click)="removeTimeSlot(dayIndex, slotIndex)"
                            [disabled]="getSlotsArray(dayIndex).length <= 1">
                      <mat-icon>delete</mat-icon>
                    </button>
                  </div>

                  <!-- Validation Error -->
                  <div class="error-message" *ngIf="slotGroup.errors?.['timeRange']">
                    {{ slotGroup.errors?.['timeRange'] }}
                  </div>
                </div>

                <!-- Empty State for Day -->
                <div class="empty-slots" *ngIf="getSlotsArray(dayIndex).length === 0">
                  <mat-icon>schedule</mat-icon>
                  <p>Nu aveți intervale de timp configurate pentru această zi.</p>
                  <button type="button" 
                          mat-raised-button 
                          color="primary" 
                          (click)="addTimeSlot(dayIndex)">
                    Adaugă primul interval
                  </button>
                </div>
              </div>
            </div>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- Activity Types Section -->
      <mat-card class="activity-types-section">
        <mat-card-header>
          <mat-card-title>Tipuri de Activități Acceptate</mat-card-title>
          <mat-card-subtitle>Selectați tipurile de activități pentru care acceptați programări</mat-card-subtitle>
        </mat-card-header>
        
        <mat-card-content>
          <div class="activity-types-grid" formArrayName="enabledActivityTypes">
            <mat-checkbox 
              *ngFor="let activityType of activityTypes; let i = index"
              [formControlName]="i"
              class="activity-type-checkbox">
              {{ activityType.name }}
            </mat-checkbox>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- Settings Section -->
      <mat-card class="settings-section">
        <mat-card-header>
          <mat-card-title>Setări Avansate</mat-card-title>
        </mat-card-header>
        
        <mat-card-content>
          <div class="settings-grid">
            <mat-checkbox formControlName="allowBookingOutsideHours">
              Permite programări în afara orelor normale
            </mat-checkbox>
            
            <mat-checkbox formControlName="requireApprovalForChanges">
              Necesită aprobare pentru modificările de program
            </mat-checkbox>
          </div>

          <div class="notification-preferences" formGroupName="notificationPreferences">
            <h4>Preferințe Notificări</h4>
            <mat-checkbox formControlName="email">Notificări prin email</mat-checkbox>
            <mat-checkbox formControlName="sms">Notificări prin SMS</mat-checkbox>
            <mat-checkbox formControlName="inApp">Notificări în aplicație</mat-checkbox>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- Action Buttons -->
      <div class="form-actions">
        <button type="button" 
                mat-stroked-button 
                (click)="previewSchedule()"
                class="preview-btn">
          <mat-icon>preview</mat-icon>
          Previzualizare Program
        </button>

        <button type="button" 
                mat-stroked-button 
                (click)="resetToDefaults()"
                class="reset-btn">
          <mat-icon>restore</mat-icon>
          Resetează la Implicit
        </button>

        <div class="spacer"></div>

        <button type="submit" 
                mat-raised-button 
                color="primary"
                [disabled]="isSaving || !availabilityForm.valid"
                class="save-btn">
          <mat-progress-spinner 
            *ngIf="isSaving" 
            diameter="20" 
            class="inline-spinner">
          </mat-progress-spinner>
          <mat-icon *ngIf="!isSaving">save</mat-icon>
          {{ isSaving ? 'Se salvează...' : 'Salvează Program' }}
        </button>
      </div>
    </form>
  </div>

  <!-- Help Section -->
  <mat-card class="help-section">
    <mat-card-header>
      <mat-card-title>
        <mat-icon>help_outline</mat-icon>
        Ghid de Utilizare
      </mat-card-title>
    </mat-card-header>
    
    <mat-card-content>
      <div class="help-content">
        <div class="help-item">
          <h4>1. Configurarea Programului</h4>
          <p>Activați zilele în care sunteți disponibil și setați intervalele orare pentru fiecare zi.</p>
        </div>
        
        <div class="help-item">
          <h4>2. Intervale Multiple</h4>
          <p>Puteți adăuga mai multe intervale pe zi (ex: 09:00-12:00 și 14:00-17:00).</p>
        </div>
        
        <div class="help-item">
          <h4>3. Copierea Programului</h4>
          <p>Folosiți opțiunea "Aplică la toate zilele" pentru a copia programul unei zile.</p>
        </div>
        
        <div class="help-item">
          <h4>4. Tipuri de Activități</h4>
          <p>Selectați tipurile de activități pentru care acceptați programări în acest program.</p>
        </div>
      </div>
    </mat-card-content>
  </mat-card>
</div>